// components/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import Navigation from './layout/Navigation';
import GenreOverview from './genre/GenreOverview';
import GenreAnalytics from './genre/GenreAnalytics';
import GameDiscovery from './discovery/GameDiscovery';
import GameInsights from './insights/GameInsights';
import RegionalAnalytics from './regional/RegionalAnalytics';
import Settings from './settings/Settings';
import ErrorBoundary from './ErrorBoundary';
import { DiscoveryProvider } from '../contexts/DiscoveryContext';
import { AnalysisProvider } from '../contexts/AnalysisContext';
import { ProgressProvider } from '../contexts/ProgressContext';
import { PublisherInsightsProvider } from '../contexts/PublisherInsightsContext';
import TrendingAnalytics from './trending/TrendingAnalytics';
import PublisherInsights from './publisher/PublisherInsights';
import SystemControl from './system/SystemControl';
import MarketIntelligence from './intelligence/MarketIntelligence';
import GameDetailsModal from './discovery/GameDetailsModal';

const Dashboard = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState('intelligence');
  const [genreData, setGenreData] = useState(null);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [genreAnalytics, setGenreAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedGameForModal, setSelectedGameForModal] = useState(null);
  const [detailsCache, setDetailsCache] = useState({});

  useEffect(() => {
    if (activeSection === 'genre') {
      fetchInitialData();
    }
  }, [activeSection]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const response = await fetch('/analytics/api/genres');
      const data = await response.json();
      
      console.log('Genre data received:', data);
      
      if (data.genres) {
        const genresData = Array.isArray(data.genres) 
          ? data.genres 
          : Object.values(data.genres);
        
        console.log('Processed genres data:', genresData);
        setGenreData(genresData);
      } else {
        console.error('No genres found in response:', data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching genre data:', error);
      setLoading(false);
    }
  };

  const handleGenreSelect = async (genre) => {
    setSelectedGenre(genre);
    setLoading(true);
    try {
      const response = await fetch(`/analytics/api/genre-analytics/${genre.label}`);
      const data = await response.json();
      
      console.log('Genre analytics received:', data);
      
      if (data.success) {
        setGenreAnalytics(data.data);
      } else {
        setGenreAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching genre analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'genre':
        return (
          <div className="p-8">
            <h2 className="text-2xl font-bold mb-6">Genre Analysis</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <GenreOverview
                data={genreData}
                onGenreSelect={handleGenreSelect}
                loading={loading}
              />
              {selectedGenre && genreAnalytics && (
                <GenreAnalytics analytics={genreAnalytics} />
              )}
            </div>
          </div>
        );
      
      case 'discovery':
        return <GameDiscovery />;
      
      case 'insights':
        return <GameInsights />;
      
      case 'publisher':
        return <PublisherInsights />;

      case 'trending':
        return <TrendingAnalytics onGameClick={(game) => setSelectedGameForModal(game)} />;
      
      case 'regional':
        return <RegionalAnalytics />;
      
      case 'settings':
        return <Settings />;

      case 'system':
        return <SystemControl />;

      case 'intelligence':
        return <MarketIntelligence />;

      default:
        return null;
    }
  };

  return (
    <ProgressProvider>
        <AnalysisProvider>
            <DiscoveryProvider>
                <PublisherInsightsProvider>
                    {/* Blue + Red + Purple layout - fills remaining height from AuthGate */}
                    <div className="flex h-full min-h-0 bg-gray-50">
                        {/* Blue - Left Navigation Sidebar */}
                        <Navigation
                            isCollapsed={isCollapsed}
                            setIsCollapsed={setIsCollapsed}
                            activeSection={activeSection}
                            setActiveSection={setActiveSection}
                        />
                        {/* Red + Purple - Main content and right sidebar */}
                        <ErrorBoundary>
                            <div className="flex-1 min-w-0 min-h-0 overflow-y-auto">
                                {renderContent()}
                            </div>
                        </ErrorBoundary>
                    </div>
                    
                    {/* Game Details Modal */}
                    {selectedGameForModal && (
                        <GameDetailsModal
                            game={selectedGameForModal}
                            onClose={() => setSelectedGameForModal(null)}
                            detailsCache={detailsCache}
                            setDetailsCache={setDetailsCache}
                        />
                    )}
                </PublisherInsightsProvider>
            </DiscoveryProvider>
        </AnalysisProvider>
    </ProgressProvider>
  );
};

export default Dashboard;
